import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api, assetUrl } from "@/lib/api";
import { ExploreHeader, ExploreHolding, ExploreError } from "@/attendee/ExploreShared";
import { Compass, MapPin, ArrowRight } from "lucide-react";

export default function ExploreActivities() {
  const navigate = useNavigate();
  const { eventId } = useApp();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");

  const load = useCallback(() => {
    let active = true;
    setState("loading");
    setData(null);
    api.exploreExcursions(eventId)
      .then((d) => { if (active) { setData(d); setState("ok"); } })
      .catch(() => { if (active) setState("error"); });
    return () => { active = false; };
  }, [eventId]);

  useEffect(() => load(), [load]);

  if (state === "error") return <ExploreError onRetry={load} />;
  if (state === "ok" && data && data.activated === false) return <ExploreHolding />;

  const items = data?.items || [];

  return (
    <div className="pt-16 px-4 pb-8">
      <ExploreHeader eyebrow="Explore" title="Activities & Excursions" subtitle="Los Cabos experiences" />

      {/* Shared booking panel */}
      {data?.booking_note && (
        <div className="att-card p-4 mb-4 border-l-2 border-[hsl(var(--pc-gold))]" data-testid="excursions-booking-panel">
          <div className="text-[11px] uppercase tracking-wide font-semibold text-[hsl(var(--pc-gold))] mb-1">Prices & booking</div>
          <p className="text-sm text-cream leading-relaxed">{data.booking_note}</p>
        </div>
      )}

      {!data && <div className="grid grid-cols-2 gap-3">{[0, 1, 2, 3].map((i) => <div key={i} className="h-40 att-card animate-pulse" />)}</div>}

      <div className="grid grid-cols-2 gap-3">
        {items.map((a) => (
          <button
            key={a.id}
            data-testid={`excursion-card-${a.id}`}
            onClick={() => navigate(`/app/explore/activities/${a.id}`)}
            className="att-card overflow-hidden text-left flex flex-col focus-visible:ring-2 focus-visible:ring-[hsl(var(--pc-gold))] outline-none"
          >
            {a.image_url && (
              <img src={assetUrl(a.image_url)} alt="" loading="lazy" decoding="async" className="h-24 w-full object-cover" />
            )}
            <div className="p-3 flex-1 flex flex-col">
              <div className="flex items-start gap-2">
                {!a.image_url && (
                  <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--pc-gold)/0.16)] text-[hsl(var(--pc-gold))] border border-[hsl(var(--pc-gold)/0.3)]"><Compass className="h-4 w-4" /></span>
                )}
                <div className="font-serif text-base text-cream leading-tight">{a.name}</div>
              </div>
              {a.badge && (
                <span className="mt-1.5 inline-flex items-center gap-1 self-start rounded-full bg-[hsl(var(--pc-gold)/0.14)] border border-[hsl(var(--pc-gold)/0.4)] px-2 py-0.5 text-[10px] font-semibold text-[hsl(var(--pc-gold))]" data-testid={`excursion-badge-${a.id}`}>
                  {a.badge}
                </span>
              )}
              <p className="text-[11px] text-cream-dim mt-1.5 line-clamp-3 leading-snug flex-1">{a.summary || a.description}</p>
              <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-[hsl(var(--pc-gold))]">Details <ArrowRight className="h-3 w-3" /></div>
            </div>
          </button>
        ))}
      </div>

      <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] text-cream-dim px-1">
        <MapPin className="h-3 w-3" /> Hotel-listed ideas — not part of the official President's Club agenda.
      </div>
    </div>
  );
}
